import React from 'react';
import Select from 'react-select';
import {FormHelperText} from '@material-ui/core';
import API from '@beardeddevops/react.api';
import {Types} from '../index';

const FEEDBACK_TYPE_LABELS = {
	1: 'Google',
	2: 'Yelp',
	3: 'Facebook',
};

export const FEEDBACK_FORM_HELPER_TEXT_STYLE = {
	minHeight: 40,
	marginTop: 4,
	marginLeft: 14,
	marginRight: 14,
};

const selectStyles = {
	control: (provided, state) => ({
		...provided,
		minHeight: 40,
		height: 40,
		borderRadius: 4,
		borderColor: state.isFocused ? '#3f51b5' : 'rgba(0, 0, 0, 0.23)',
		boxShadow: state.isFocused ? '0 0 0 1px #3f51b5' : 'none',
		'&:hover': {
			borderColor: state.isFocused ? '#3f51b5' : 'rgba(0, 0, 0, 0.87)',
		},
	}),
	valueContainer: (provided) => ({
		...provided,
		padding: '0 14px',
		height: 38,
	}),
	indicatorsContainer: (provided) => ({
		...provided,
		height: 38,
	}),
	input: (provided) => ({
		...provided,
		margin: 0,
		padding: 0,
	}),
	placeholder: (provided) => ({
		...provided,
		color: 'rgba(0, 0, 0, 0.54)',
	}),
};

export const loadFeedbackLocations = async () => {
	const model = new API.BuildClass(Types.Feedback_Locations._name, API.RequestType.READ_ALL);
	const response = await model.submit(null, 'GET_ALL');
	return response?.items ?? [];
};

export const filterFeedbackLocationsForRegion = (locations, regionPkey) => {
	if (!regionPkey || !Array.isArray(locations)) {
		return [];
	}

	const normalizedRegionPkey = Number(regionPkey);

	return locations.filter((location) => {
		const isWorking = location.working === true || location.working === 1 || location.working === '1';
		return Number(location.region) === normalizedRegionPkey && isWorking;
	});
};

export const findFeedbackLocationByPkey = (locations, feedbackLocationPkey) => {
	if (!feedbackLocationPkey || !Array.isArray(locations)) {
		return null;
	}

	const normalizedPkey = Number(feedbackLocationPkey);

	return locations.find((location) => Number(location.pkey) === normalizedPkey) || null;
};

export const getResolvedFeedbackLink = (zone) => {
	return (zone?.link || '').trim();
};

export const nextInternalNameForLocation = (currentName, previousLocation, nextLocation) => {
	const current = (currentName || '').trim();
	const previousDescription = (previousLocation?.description || '').trim();
	const nextDescription = (nextLocation?.description || '').trim();
	if (!nextDescription) {
		return current;
	}
	if (!current || current === previousDescription) {
		return nextDescription;
	}
	return current;
};

export const getFeedbackLocationOptionLabel = (location) => {
	const typeLabel = FEEDBACK_TYPE_LABELS[location.feedback_type] || 'Other';
	const description = (location.description || '').trim() || 'Unnamed location';
	const display = (location.display || '').trim();

	if (display && display !== description) {
		return `${description} - ${display} (${typeLabel})`;
	}

	return `${description} (${typeLabel})`;
};

export const buildFeedbackLocationOptions = (locations) => {
	return locations.map((location) => ({
		value: location.pkey,
		label: getFeedbackLocationOptionLabel(location),
		location,
	}));
};

const FeedbackLocationSelect = ({
	locations = [],
	selectedRegionPkey,
	feedbackLocationPkey,
	onChange,
	disabled = false,
}) => {
	const filteredLocations = filterFeedbackLocationsForRegion(locations, selectedRegionPkey);
	const options = buildFeedbackLocationOptions(filteredLocations);
	const selectedOption = feedbackLocationPkey
		? options.find((option) => option.value === Number(feedbackLocationPkey)) || null
		: null;

	return (
		<div className="form-group" style={{marginBottom: 0}}>
			<div style={{position: 'relative', marginTop: 8}}>
				<span style={{
					position: 'absolute',
					top: -6,
					left: 10,
					fontSize: 12,
					backgroundColor: '#fff',
					padding: '0 4px',
					color: 'rgba(0, 0, 0, 0.54)',
					zIndex: 1,
					lineHeight: 1,
				}}>
					Feedback Location
				</span>
				<Select
					inputId="feedback-location-select"
					placeholder={filteredLocations.length ? 'Select a location...' : 'No working locations'}
					value={selectedOption}
					onChange={(option) => onChange(option ? option.location : null)}
					options={options}
					isClearable
					isDisabled={disabled || filteredLocations.length === 0}
					styles={selectStyles}
				/>
			</div>
			<FormHelperText style={FEEDBACK_FORM_HELPER_TEXT_STYLE}>
				{filteredLocations.length
					? 'Associates this zone with a /feedback location. Does not set the review URL.'
					: 'Add working locations for this region on /feedback'}
			</FormHelperText>
		</div>
	);
};

export default FeedbackLocationSelect;
